// src/components/proposal/StructuredSectionEditor.tsx
// Human-readable, structure-preserving editor for a generated section.
//
// Generated sections are stored as structured JSON. Rather than make the user
// hand-edit a raw JSON blob (which reads like markup), we flatten the section into
// labeled prose fields they can edit naturally, then write each edit back into the
// JSON so the formatted preview still renders. If the content isn't JSON (legacy
// prose), we fall back to a single plain-text box.

import React, { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  flattenEditableFields,
  setFieldByPath,
  parseMaybeJson,
  EditField,
} from '../../lib/section-content-utils';

interface StructuredSectionEditorProps {
  content: string;
  onChange: (newContent: string) => void;
}

// Auto-size a textarea to its content (readable, no scrollbars for short fields).
function rowsFor(text: string, min = 2, max = 18): number {
  const lines = (text || '').split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 90)), 0);
  return Math.min(max, Math.max(min, lines));
}

export const StructuredSectionEditor: React.FC<StructuredSectionEditorProps> = ({ content, onChange }) => {
  const isStructured = useMemo(() => !!parseMaybeJson(content), [content]);
  const fields = useMemo(() => (isStructured ? flattenEditableFields(content) : []), [content, isStructured]);

  // Local working copy so typing is smooth; we commit on blur.
  const [draft, setDraft] = useState<string>(content);

  // Plain-text (non-JSON) sections: a single readable textarea.
  if (!isStructured) {
    return (
      <textarea
        defaultValue={content}
        onBlur={e => onChange(e.target.value)}
        rows={rowsFor(content, 6, 24)}
        className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm leading-relaxed focus:ring-2 focus:ring-slate-500 focus:border-transparent"
      />
    );
  }

  const commitField = (field: EditField, value: string) => {
    const next = setFieldByPath(draft, field.path, value, field.kind);
    setDraft(next);
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-bw-muted">
        Edit each part in plain language — the formatting is reapplied when you switch back to Preview.
      </p>
      {fields.map((field, idx) => (
        <div key={`${field.label}-${idx}`}>
          <label className="flex items-center gap-2 text-xs font-medium text-bw-muted mb-1">
            <span>{field.label}</span>
            {field.flagged && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-fit-moderate bg-fit-moderate-bg border border-amber-300 rounded-full px-1.5 py-0.5">
                <AlertTriangle className="w-3 h-3" /> needs data
              </span>
            )}
          </label>
          <textarea
            defaultValue={field.value}
            onBlur={e => commitField(field, e.target.value)}
            rows={rowsFor(field.value, field.kind === 'list' ? 3 : 2)}
            placeholder={field.kind === 'list' ? 'One item per line…' : ''}
            className={`w-full px-3 py-2 border rounded-lg text-sm leading-relaxed focus:ring-2 focus:border-transparent ${
              field.flagged
                ? 'border-amber-300 bg-fit-moderate-bg/40 focus:ring-amber-400'
                : 'border-bw-border focus:ring-slate-500'
            }`}
          />
          {field.kind === 'list' && (
            <p className="text-[11px] text-bw-muted mt-0.5">List — one item per line.</p>
          )}
        </div>
      ))}
    </div>
  );
};

export default StructuredSectionEditor;
